import { CUSTOMER_CONFIGURATIONS_SPACE_PARAMETER_SUPPORT } from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import { useSpaceParameterControlledFilter } from '@src/components/AppLayout/SpaceParameters';
import { useCounterpartyFilter, useFeatureFlag, useModeFilter, usePricingTierFilters, useRoleAuth } from '@src/hooks';
import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers';
import {
  ACTION,
  BlotterDensity,
  DEFAULT_BULK_EDIT_MAX_ROWS,
  DEFAULT_MAX_ROWS,
  DEFAULT_PAGINATION_SIZE,
  EMPTY_ARRAY,
  EntityAdmin,
  type OnCustomSaveEntityHandler,
} from '@talos/kyoko';
import { isNil } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useCustomerConfigurationsEntityAdminConfig } from './useCustomerConfigurationsEntityAdminConfig';
import {
  CUSTOMER_CONFIGURATIONS_BULK_EDIT_FIELDS,
  CUSTOMER_CONFIGURATIONS_FILTERABLE_SERVER_FIELDS,
  getCustomerConfigurationsPatchDeletePath,
  type CustomerConfigurationDrawerRecord,
  type CustomerConfigurationWithDisplayName,
} from './utils';

export const CustomerConfigurations = () => {
  const { isAuthorized } = useRoleAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const { getConfig } = useOrgConfiguration();
  const { showCustomerTiering } = useFeatureFlag();
  const {
    columns,
    createCustomerFromDrawerRecord,
    createDrawerOptions,
    customersByName,
    editDrawerOptions,
    getCustomerConfigurationForPatchPost,
    getDisplayName,
    invalidateCustomerConfigurationQueries,
    serverDataToRecordMappingFunc,
  } = useCustomerConfigurationsEntityAdminConfig();

  const modeFilter = useModeFilter();
  const counterpartyFilter = useCounterpartyFilter({ label: 'Customer', nullUntilLoaded: true });
  const { allInTierFilter, feeTierFilter, symbolGroupFilter } = usePricingTierFilters();
  const availableFilterableProperties = useMemo(() => {
    const properties = showCustomerTiering
      ? [modeFilter, counterpartyFilter, allInTierFilter, feeTierFilter, symbolGroupFilter]
      : [modeFilter, counterpartyFilter];

    // In order to populate the blotter row with the DisplayName, we need to have the customersByName map ready.
    if (customersByName == null) {
      return undefined;
    }
    // As per the Entity Admin API, undefined is used to indicate a loading state
    return properties.every(prop => !isNil(prop)) ? properties : undefined;
  }, [
    showCustomerTiering,
    modeFilter,
    counterpartyFilter,
    allInTierFilter,
    feeTierFilter,
    symbolGroupFilter,
    customersByName,
  ]);
  const { externallyControlledFilter, filterableProperties } = useSpaceParameterControlledFilter(
    CUSTOMER_CONFIGURATIONS_SPACE_PARAMETER_SUPPORT,
    availableFilterableProperties
  );

  const handleCustomSaveEntity = useCallback<
    OnCustomSaveEntityHandler<CustomerConfigurationDrawerRecord, CustomerConfigurationWithDisplayName>
  >(
    (_dirtyEntities: CustomerConfigurationDrawerRecord[], entity: CustomerConfigurationDrawerRecord, context) => {
      if (!context.isCreate) {
        return false;
      }

      return createCustomerFromDrawerRecord(entity).then(() => {
        // Remount EntityAdmin to force a fresh fetch
        setRefreshKey(prevKey => prevKey + 1);
        return true;
      });
    },
    [createCustomerFromDrawerRecord]
  );

  return (
    <EntityAdmin<CustomerConfigurationWithDisplayName, CustomerConfigurationDrawerRecord>
      key={refreshKey}
      title="Customer Configurations"
      subtitle="View and manage customer configurations."
      entityName="Customer"
      path="/customer-configurations"
      getPatchPath={getCustomerConfigurationsPatchDeletePath}
      entityIDField="Counterparty"
      density={BlotterDensity.Comfortable}
      columns={columns}
      drawerOptions={createDrawerOptions}
      getEntityDrawerOptions={entity => (entity == null ? createDrawerOptions : editDrawerOptions)}
      filterableProperties={filterableProperties}
      externallyControlledFilter={externallyControlledFilter}
      filterableServerFields={CUSTOMER_CONFIGURATIONS_FILTERABLE_SERVER_FIELDS}
      allowModeSwitch={{
        isDisabled: () => !isAuthorized(ACTION.EDIT_CUSTOMER_CONFIGURATIONS),
      }}
      allowAddEntity={isAuthorized(ACTION.EDIT_CUSTOMER_CONFIGURATIONS)}
      allowEditEntity={isAuthorized(ACTION.EDIT_CUSTOMER_CONFIGURATIONS)}
      allowDeleteEntity={false}
      getEntityDisplayName={getDisplayName}
      getEntityForPatchPost={getCustomerConfigurationForPatchPost}
      onCustomSaveEntity={handleCustomSaveEntity}
      onOperationSettled={invalidateCustomerConfigurationQueries}
      newEntityDefault={() => ({ Name: '', DisplayName: '' })}
      serverDataToRecordMappingFunc={serverDataToRecordMappingFunc}
      persistKey="customer-configurations-with-display-name"
      blotterRowCount={getConfig(OrgConfigurationKey.CustomerConfigurationRowsMax, DEFAULT_MAX_ROWS)}
      paginationSize={getConfig(OrgConfigurationKey.CustomerConfigurationsPageSize, DEFAULT_PAGINATION_SIZE)}
      allowBulkEdit={true} // allowEditEntity must also be true
      bulkEditFields={showCustomerTiering ? CUSTOMER_CONFIGURATIONS_BULK_EDIT_FIELDS : EMPTY_ARRAY}
      maxBulkEditRows={getConfig(OrgConfigurationKey.EntityAdminMaxBulkEditRows, DEFAULT_BULK_EDIT_MAX_ROWS)}
    />
  );
};
